import React, { useState, useEffect } from 'react';
import './CustomerManagement.css';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Grid,
  Box,
  Typography,
  useTheme,
  Stack, IconButton, alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import {EnhancedDataGridWrapper, SearchCondition} from '../Common';
import Swal from 'sweetalert2';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import Message from '../../utils/message/Message';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HelpModal from "../Common/HelpModal";
import {deleteVendor, getVendors, saveVendor} from "../../api/standardInfo/customerApi";
import {fetchGridCodesByCodeClassId, useSelectionModel} from "../../utils/grid/useGridRow";
import { useExcelUpload, useExcelTemplateInfo } from '../../utils/excelUploadApi';
import { downloadExcelTemplate } from '../../utils/excelImportUtils';
import BusinessRegNoModal from './BusinessRegNoModal';
import { removeHyphens, formatBusinessRegNo, formatPhoneNumber } from '../../api/businessRegNoApi';

const CustomerManagement = (props) => {
  // 현재 테마 가져오기
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  // 도메인별 색상 설정
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };

  const getBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#f5e8d7';
    }
    return isDarkMode ? '#1e3a5f' : '#e0e0e0';
  };

  // React Hook Form 설정
  const { control, handleSubmit, reset,getValues } = useForm({
    defaultValues: {
      vendorId: '',
      vendorName: '',
      ceoName: '',
      businessRegNo: '',
      businessType: '',
    }
  });

  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [vendorList, setVendorList] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]); // 수정된 필드만 저장하는 객체
  const [addRows,setAddRows] = useState([]);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [currentEditingRow, setCurrentEditingRow] = useState(null);

  // 엑셀 업로드 관련 훅
  const { uploadExcel, loading: uploadLoading } = useExcelUpload();
  const { templateInfo } = useExcelTemplateInfo('VENDOR');

  //거래처 유형 리스트 불러오기
  const [vendorTypeOptions, setVendorTypeOptions] = useState([]);

  const {
    selectionModel,            // 선택된 ID 배열
    onSelectionModelChange,    // DataGrid에 넘길 핸들러
    removeSelectedRows
  } = useSelectionModel([], setAddRows, setUpdatedRows, setVendorList);

  useEffect(() => {
    fetchGridCodesByCodeClassId("CD20250331110039125",setVendorTypeOptions)
  }, []);

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    // 약간의 딜레이를 주어 DOM 요소가 완전히 렌더링된 후에 그리드 데이터를 설정
    const timer = setTimeout(() => {

      getVendors(
          getValues()
      ).then((data) => {
        if (data.errors) {
        } else {
          const rowsWithId = data.map((row, index) => ({
            ...row,
            id: row.vendorId  // 또는 row.factoryId || index + 1
          }));
          setVendorList(rowsWithId);
        }
        setIsLoading(false);
      })
          .catch((err) => {
            setIsLoading(false);
          });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // 거래처 목록 그리드 컬럼 정의
  const customerColumns = [
    { field: 'vendorId', headerName: '거래처코드', width: 140 },
    {
      field: 'vendorName',
      headerName: '거래처명',
      width: 120 ,
      editable: true,
      renderCell: (params) => {
        // 새로 추가된 행인지 확인 (id가 NEW_로 시작하는지)
        const isNewRow = params.row.id?.toString().startsWith('NEW_');

        // 새로 추가된 행이고 값이 없는 경우에만 '필수 입력' 표시
        const showRequired = isNewRow && (!params.value || params.value === '');

        return (
            <Typography variant="body2" sx={{color: showRequired ? '#f44336' : 'inherit'}}>
              {showRequired ? '필수 입력' : params.value || ''}
            </Typography>
        );
      }
    },
    {
      field: 'vendorType',
      headerName: '거래처 유형',
      width: 100,
      editable: true,
      type: 'singleSelect',
      valueOptions: vendorTypeOptions
    },
    {
      field: 'businessRegNo',
      headerName: '사업자등록 번호',
      width: 180,
      editable: false,
      renderCell: (params) => {
        // 새로 추가된 행인지 확인 (id가 NEW_로 시작하는지)
        const isNewRow = params.row.id?.toString().startsWith('NEW_');

        // 새로 추가된 행이고 값이 없는 경우에만 '필수 입력' 표시
        const showRequired = isNewRow && (!params.value || params.value === '');

        return (
            <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
                onDoubleClick={() => handleBusinessRegNoDoubleClick(params.row)}
            >
              <Typography variant="body2" sx={{color: showRequired ? '#f44336' : 'inherit', width: '100%'}}>
                {showRequired ? '필수 입력 (더블클릭)' : params.value || '더블클릭하여 조회'}
              </Typography>
            </Box>
        );
      },
      valueFormatter: (params) => {
        if (!params.value) return '';

        // 하이픈 자동 제거 및 포맷팅
        let value = removeHyphens(params.value);

        // 전화번호 패턴인지 확인 (010, 011, 016, 017, 018, 019로 시작하는 11자리)
        if (value.startsWith('010') || value.startsWith('011') ||
            value.startsWith('016') || value.startsWith('017') ||
            value.startsWith('018') || value.startsWith('019')) {
          return formatPhoneNumber(value);
        } else if (value.length === 10) {
          // 사업자등록번호 패턴 (10자리)
          return formatBusinessRegNo(value);
        }

        return params.value;
      }
    },
    { field: 'ceoName', headerName: '대표자명', width: 100, editable: true },
    { field: 'businessType', headerName: '업종/업태', width: 100, editable: true },
    { field: 'address', headerName: '주소', width: 150 ,editable: true  },
    { field: 'telNo', headerName: '전화번호', width: 130, editable: true },
    { field: 'createUser', headerName: '작성자', width: 90, flex: 1},
    { field: 'createDate', headerName: '작성일', width: 150, flex: 1},
    { field: 'updateUser', headerName: '수정자', width: 90, flex: 1},
    { field: 'updateDate', headerName: '수정일', width: 150, flex: 1}
  ];

  // 초기화 함수
  const handleReset = () => {
    reset({
      vendorId: '',
      vendorName: '',
      ceoName: '',
      businessRegNo: '',
      businessType: '',
    });
  };

  // 검색 실행 함수
  const handleSearch = (data) => {
    // 검색후 업데이트 로우 초기화
    setUpdatedRows([]);
    setAddRows([]);

    getVendors(
        data
    ).then((data) => {
      if (data.errors) {
      } else {
        const rowsWithId = data.map((row, index) => ({
          ...row,
          id: row.vendorId  // 또는 row.factoryId || index + 1
        }));
        setVendorList(rowsWithId);
        // setRefreshKey(prev => prev + 1);
      }
      setIsLoading(false);
    }).catch((err) => {
      setIsLoading(false);
    });
  };

  const transformRowForMutation = (row) => ({
    vendorName: row.vendorName,
    vendorType: row.vendorType,
    businessRegNo: row.businessRegNo,
    ceoName: row.ceoName,
    businessType: row.businessType,
    address: row.address,
    telNo: row.telNo,
  });

  const transformRowForUpdate = (row) => ({
    vendorId: row.vendorId,
    vendorName: row.vendorName,
    vendorType: row.vendorType,
    businessRegNo: row.businessRegNo,
    ceoName: row.ceoName,
    businessType: row.businessType,
    address: row.address,
    telNo: row.telNo,
  });

  const handleSave = () => {
    if (!selectionModel || selectionModel.length === 0) {
      Message.showWarning('저장할 데이터를 선택해주세요.');
      return;
    }

    const selectedAddRows = addRows.filter((row) => selectionModel.includes(row.id));
    const selectedUpdatedRows = updatedRows.filter((row) => selectionModel.includes(row.id));
    const allRows = [...selectedAddRows, ...selectedUpdatedRows];

    if (allRows.length === 0) {
      Message.showWarning('선택한 항목에 저장할 변경사항이 없습니다.');
      return;
    }

    // 필수 필드 검증 매핑
    const validateRequiredFields = (rows, fieldMapping) => {
      const emptyFields = [];

      for (const row of rows) {
        for (const [field, displayName] of Object.entries(fieldMapping)) {
          if (!row[field] || row[field].toString().trim() === '') {
            emptyFields.push({
              row: row.id || '신규',
              field: displayName
            });
          }
        }
      }
      return emptyFields;
    };

    // 필수 필드 정의
    const requiredFields = {
      vendorName: '거래처명',
      businessRegNo: '사업자등록번호'
    };

    // 선택된 행들의 필수 필드 검증
    const emptyRequiredFields = validateRequiredFields(allRows, requiredFields);

    if (emptyRequiredFields.length > 0) {
      const errorMessage = emptyRequiredFields
          .map(item => `${item.row}: ${item.field}`)
          .join('\n');

      Message.showError(`다음 필수 필드를 입력해주세요:\n${errorMessage}`);
      return;
    }

    const addMutations = selectedAddRows.map(row => transformRowForMutation(row));
    const updateMutations = selectedUpdatedRows.map(row => transformRowForUpdate(row));

    // key값이 일치하지 않아서 저장이 안되는 수정 사항 - 2025.08.12 HJS
    const variablesForSave = {
      createdRows: addMutations,   // 이 키에는 'addMutations' 배열이 값으로 들어갑니다.
      updatedRows: updateMutations  // 이 키에는 'updateMutations' 배열이 값으로 들어갑니다.
    };

    saveVendor(variablesForSave)
        .then((result) => {
          if (result.errors) {
            Message.showError(result.errors.map(error => error.message).join(', '));
          } else {
            Message.showSuccess('저장되었습니다.');
            handleSearch(getValues());
            setUpdatedRows([]);
            setAddRows([]);
          }
        })
        .catch((error) => {
          Message.showError(error.message || '저장 중 오류가 발생했습니다.');
        });
  };

  const handleDelete = () => {
    if (!selectionModel || selectionModel.length === 0) {
      Message.showWarning('삭제할 데이터를 선택해주세요.');
      return;
    }

    Swal.fire({
      title: '삭제 확인',
      text: `선택한 ${selectionModel.length}개의 데이터를 삭제하시겠습니까?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '삭제',
      cancelButtonText: '취소'
    }).then((result) => {
      if (result.isConfirmed) {
        const toDelete = selectionModel
            .map(id => vendorList.find(row => row.id === id))
            .filter(row => row && row.id && !row.id?.toString().startsWith('NEW_'))
            .map(row => row.vendorId)
            .filter(vendorId => vendorId);

        if (toDelete.length === 0) {
          // 새로 추가된 행만 선택된 경우
          removeSelectedRows();
          Message.showSuccess('선택한 항목이 제거되었습니다.');
          return;
        }

        deleteVendor(toDelete)
            .then((result) => {
              if (result && result.errors) {
                Message.showError(result.errors.map(error => error.message).join(', '));
              } else {
                Message.showSuccess('삭제되었습니다.');

                // 선택 상태 초기화
                removeSelectedRows();
                // 거래처 데이터만 새로고침
                handleSearch(getValues());
              }
            })
            .catch((error) => {
              console.error('삭제 오류:', error);
              Message.showError(error?.message || '삭제 중 오류가 발생했습니다.');
            });
      }
    });
  };

  const handleAdd = () => {
    const newId = `NEW_${Date.now()}`;
    const newRow = {
      id: newId,
      vendorId: '',
      vendorName: '',
      vendorType: '',
      businessRegNo: '',
      ceoName: '',
      businessType: '',
      address: '',
      telNo: '',
      isNew: true
    };

    setVendorList(prev => [newRow, ...prev]);
    setAddRows(prev => [...prev, newRow]);
  };

  // 템플릿 다운로드 함수
  const handleTemplateDownload = async () => {
    if (templateInfo && templateInfo.columns && Array.isArray(templateInfo.columns)) {
      try {
        const success = downloadExcelTemplate(
          templateInfo.columns,
          templateInfo.templateName || 'VENDOR',
          templateInfo.sampleData || []
        );
        
        if (success) {
          Message.showSuccess('템플릿 다운로드가 완료되었습니다.');
        }
      } catch (error) {
        console.error('템플릿 다운로드 오류:', error);
        Message.showError('템플릿 다운로드 중 오류가 발생했습니다.');
      }
    } else {
      Message.showWarning('템플릿 정보를 불러올 수 없습니다.');
    }
  };

  // 엑셀 업로드 함수
  const handleExcelUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const response = await uploadExcel(file, 'VENDOR', null, {});
        
        if (response.success) {
          // 업로드 성공 후 그리드 새로고침
          handleSearch(getValues());
          Message.showSuccess(`업로드 완료: 총 ${response.totalRows}개 중 ${response.successRows}개 성공`);
        }
      } catch (error) {
        console.error('엑셀 업로드 오류:', error);
        Message.showError('엑셀 업로드 중 오류가 발생했습니다.');
      }
    };

    input.click();
  };

  // 사업자등록번호 더블클릭 핸들러
  const handleBusinessRegNoDoubleClick = (row) => {
    setCurrentEditingRow(row);
    setIsBusinessModalOpen(true);
  };

  // 사업자등록번호 선택 핸들러
  const handleBusinessRegNoSelect = (selectedData) => {
    if (!currentEditingRow) return;

    const updatedRow = {
      ...currentEditingRow,
      businessRegNo: selectedData.businessRegNo,
      vendorName: selectedData.vendorName || currentEditingRow.vendorName,
      ceoName: selectedData.ceoName || currentEditingRow.ceoName,
      address: selectedData.address || currentEditingRow.address,
      businessType: selectedData.businessType || currentEditingRow.businessType,
      vendorType: selectedData.vendorType || currentEditingRow.vendorType
    };

    // 그리드 데이터 업데이트
    setVendorList(prev => prev.map(row =>
      row.id === currentEditingRow.id ? updatedRow : row
    ));

    // 새로 추가된 행인지 확인 (id가 NEW_로 시작하는지)
    const isNewRow = currentEditingRow.id?.toString().startsWith('NEW_');

    if (isNewRow) {
      // 새로 추가된 행의 경우 addRows에 업데이트
      setAddRows(prev => prev.map(row =>
        row.id === currentEditingRow.id ? updatedRow : row
      ));
    } else {
      // 기존 행의 경우 updatedRows에 추가
      setUpdatedRows(prev => {
        const existingIndex = prev.findIndex(row => row.vendorId === updatedRow.vendorId);
        if (existingIndex >= 0) {
          // 이미 수정된 행이 있으면 업데이트
          const updated = [...prev];
          updated[existingIndex] = updatedRow;
          return updated;
        } else {
          // 새로 수정된 행이면 추가
          return [...prev, updatedRow];
        }
      });
    }

    setIsBusinessModalOpen(false);
    setCurrentEditingRow(null);
  };

  function handleProcessRowUpdate(newRow, oldRow) {
    // 사업자등록번호 자동 포맷팅 처리
    if (newRow.businessRegNo && newRow.businessRegNo !== oldRow.businessRegNo) {
      let value = removeHyphens(newRow.businessRegNo);

      // 전화번호 패턴인지 확인 (010, 011, 016, 017, 018, 019로 시작하는 11자리)
      if (value.startsWith('010') || value.startsWith('011') ||
          value.startsWith('016') || value.startsWith('017') ||
          value.startsWith('018') || value.startsWith('019')) {
        newRow.businessRegNo = formatPhoneNumber(value);
      } else if (value.length === 10) {
        // 사업자등록번호 패턴 (10자리)
        newRow.businessRegNo = formatBusinessRegNo(value);
      }
    }

    // 행이 수정되었는지 확인
    const isModified = Object.keys(newRow).some(key => newRow[key] !== oldRow[key]);

    if (isModified) {
      // 새로 추가된 행인지 확인 (id가 NEW_로 시작하는지)
      const isNewRow = newRow.id?.toString().startsWith('NEW_');

      if (isNewRow) {
        // 새로 추가된 행의 경우 addRows에 업데이트
        setAddRows(prev => prev.map(row =>
          row.id === newRow.id ? newRow : row
        ));
      } else {
        // 기존 행의 경우 updatedRows에 추가
        setUpdatedRows(prev => {
          const existingIndex = prev.findIndex(row => row.vendorId === newRow.vendorId);
          if (existingIndex >= 0) {
            // 이미 수정된 행이 있으면 업데이트
            const updated = [...prev];
            updated[existingIndex] = newRow;
            return updated;
          } else {
            // 새로 수정된 행이면 추가
            return [...prev, newRow];
          }
        });
      }
    }

    return newRow;
  }

  // 그리드 버튼 배열
  const customerGridButtons = [
    {
      label: '등록',
      icon: <AddIcon />,
      onClick: handleAdd,
      color: 'primary',
      variant: 'contained'
    },
    {
      label: '저장',
      icon: <SaveIcon />,
      onClick: handleSave,
      color: 'success',
      variant: 'contained'
    },
    {
      label: '삭제',
      icon: <DeleteIcon />,
      onClick: handleDelete,
      color: 'error',
      variant: 'outlined'
    },
    {
      label: '템플릿 다운로드',
      icon: <GetAppIcon />,
      onClick: handleTemplateDownload,
      color: 'secondary',
      variant: 'outlined',
      disabled: !templateInfo
    },
    {
      label: '엑셀 업로드',
      icon: <CloudUploadIcon />,
      onClick: handleExcelUpload,
      color: 'info',
      variant: 'outlined',
      disabled: uploadLoading
    }
  ];

  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
        {/* 페이지 제목 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 600,
                color: getTextColor()
              }}
          >
            거래처관리
          </Typography>
          <IconButton
              onClick={() => setIsHelpModalOpen(true)}
              sx={{
                ml: 1,
                color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: isDarkMode
                      ? alpha(theme.palette.primary.light, 0.1)
                      : alpha(theme.palette.primary.main, 0.05)
                }
              }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Box>

        {/* 검색 조건 영역 - 공통 컴포넌트 사용 */}
        <SearchCondition
            onSearch={handleSubmit(handleSearch)}
            onReset={handleReset}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Controller
                name="vendorId"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="거래처코드"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="거래처코드를 입력하세요"
                    />
                )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
                name="vendorName"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="거래처명"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="거래처명을 입력하세요"
                    />
                )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
                name="ceoName"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="대표자명"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="대표자명을 입력하세요"
                    />
                )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
                name="businessRegNo"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="사업자등록번호"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="사업자등록번호를 입력하세요"
                    />
                )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
                name="businessType"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="업종"
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="업종을 입력하세요"
                    />
                )}
            />
          </Grid>
        </SearchCondition>

        {/* 그리드 영역 */}
        {!isLoading && (
            <Grid item xs={12} md={6}>
              <EnhancedDataGridWrapper
                  title="거래처 목록"
                  rows={vendorList}
                  columns={customerColumns}
                  buttons={customerGridButtons}
                  height={640}
                  gridProps={{
                    checkboxSelection: true,
                    onSelectionModelChange: onSelectionModelChange,
                    editMode: 'cell',
                    onProcessUpdate: handleProcessRowUpdate
                  }}
                  tabId={props.tabId + "-customer"}
              />
            </Grid>
        )}

        {/* 도움말 모달 */}
        <HelpModal
            open={isHelpModalOpen}
            onClose={() => setIsHelpModalOpen(false)}
            title="거래처관리 도움말"
        >
          <Typography variant="body2" color={getTextColor()}>
            • 거래처관리에서는 거래처 및 고객사의 정보를 등록, 수정, 삭제할 수 있습니다.
          </Typography>
          <Typography variant="body2" color={getTextColor()}>
            • 거래처 등록 시 거래처명, 연락처, 주소 등의 정보를 입력할 수 있습니다.
          </Typography>
          <Typography variant="body2" color={getTextColor()}>
            • 사업자등록번호 셀을 더블클릭하면 사업자 조회 팝업이 열립니다.
          </Typography>
        </HelpModal>

        {/* 사업자등록번호 조회 모달 */}
        <BusinessRegNoModal
            open={isBusinessModalOpen}
            onClose={() => {
              setIsBusinessModalOpen(false);
              setCurrentEditingRow(null);
            }}
            onSelect={handleBusinessRegNoSelect}
            rowData={currentEditingRow}
        />
      </Box>
  );
};

export default CustomerManagement;